import { useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { useDebouncedState, useIntersectionObserver } from '@react-hookz/web'
import {
    type OnChangeFn,
    type RowSelectionState,
    flexRender,
    getCoreRowModel,
    useReactTable,
} from '@tanstack/react-table'

import { api, getApiErrorMessage, getNextPageParam } from '~/api'
import QueryMatcher from '~/components/QueryMatcher'
import { useAppSelector } from '~/store'
import { getTranslationI18n } from '~/utils/i18n'
import { giftWithPurchaseQueryKeys } from '../queries/queryKeys'

export type Props = {
    open: boolean
    selectedProducts?: RowSelectionState
    fetchLimit?: number
    onAdd?: (
        products: Array<{
            id: number
            cover_image_url: string
            translations: Record<string, { name: string }>
        }>
    ) => void
    onClose: () => void
    onSelectedProductsChange?: OnChangeFn<RowSelectionState>
}

export default function DialogSearchProducts({
    open,
    selectedProducts = {},
    fetchLimit = 25,
    onAdd,
    onClose,
    onSelectedProductsChange,
}: Props) {
    const userId = useAppSelector((state) => state.initial.user.id)
    const { t } = useTranslation(['common', 'gift_with_purchase'])

    const [search, setSearch] = useDebouncedState('', 1000)
    const selectedProductIds = new Set(Object.keys(selectedProducts).map(Number))
    const hasSelectedProducts = selectedProductIds.size > 0

    const tableContainerRef = useRef<HTMLDivElement>(null)
    const lastRowRef = useRef<HTMLTableRowElement>(null)
    const entry = useIntersectionObserver(lastRowRef.current, {
        root: tableContainerRef.current,
        threshold: [0.1],
    })

    const query = api.users.products.useInfiniteQuery(
        giftWithPurchaseQueryKeys.productSearch({ limit: fetchLimit, search }),
        ({ pageParam = 0 }) => ({
            params: {
                id: userId,
            },
            query: {
                status: 'active',
                search,
                limit: fetchLimit,
                offset: pageParam * fetchLimit,
            },
        }),
        {
            getNextPageParam: (lastPage) => getNextPageParam(lastPage.body.next, fetchLimit),
            enabled: open,
        }
    )
    const data = useMemo(
        () => query.data?.pages.flatMap((page) => page.body.results) ?? [],
        [query.data]
    )
    const hasProducts = data.length > 0

    const table = useReactTable({
        getCoreRowModel: getCoreRowModel(),
        getRowId: (row) => row.id.toString(),
        data,
        state: {
            rowSelection: selectedProducts,
        },
        columns: [
            {
                id: 'checkbox',
                header: ({ table }) => (
                    <Checkbox
                        disabled={!hasProducts}
                        color="secondary"
                        indeterminate={table.getIsSomeRowsSelected()}
                        checked={table.getIsAllRowsSelected()}
                        onChange={table.getToggleAllRowsSelectedHandler()}
                    />
                ),
                cell: ({ row }) => (
                    <Checkbox
                        color="secondary"
                        checked={row.getIsSelected()}
                        onChange={row.getToggleSelectedHandler()}
                    />
                ),
            },
            {
                accessorKey: 'cover_image_url',
                header: '',
                cell: ({ getValue }) => <img src={getValue()} style={{ width: 40 }} />,
            },
            {
                accessorKey: 'translations',
                header: t('common:name'),
                cell: ({ getValue }) => getTranslationI18n('name', getValue()),
            },
        ],
        onRowSelectionChange: onSelectedProductsChange,
    })

    useEffect(() => {
        if (entry?.isIntersecting) {
            query.fetchNextPage()
        }
    }, [entry?.isIntersecting, query.fetchNextPage])

    return (
        <Dialog
            fullWidth
            maxWidth="sm"
            open={open}
            TransitionProps={{
                onExited: () => {
                    setSearch('')
                    table.resetRowSelection()
                    table.resetPageIndex()
                },
            }}
            onClose={onClose}
        >
            <DialogTitle>{t('gift_with_purchase:dialog_search_products.add_product')}</DialogTitle>

            <DialogContent>
                <Stack gap={2} sx={{ pt: 1 }}>
                    <TextField
                        fullWidth
                        size="small"
                        label={t('gift_with_purchase:dialog_search_products.field_search_product')}
                        onChange={(e) => setSearch(e.target.value)}
                    />

                    <TableContainer ref={tableContainerRef} sx={{ maxHeight: '70dvh' }}>
                        <Table stickyHeader>
                            <TableHead>
                                {table.getHeaderGroups().map((headerGroup) => (
                                    <TableRow key={headerGroup.id}>
                                        {headerGroup.headers.map((header) => (
                                            <TableCell
                                                key={header.id}
                                                sx={{ textTransform: 'capitalize' }}
                                                padding={
                                                    header.id === 'checkbox'
                                                        ? 'checkbox'
                                                        : undefined
                                                }
                                            >
                                                {flexRender(
                                                    header.column.columnDef.header,
                                                    header.getContext()
                                                )}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                ))}
                            </TableHead>
                            <TableBody>
                                <QueryMatcher
                                    query={query}
                                    Loading={Array.from({
                                        length: table.getState().pagination.pageSize,
                                    }).map((_, index) => (
                                        <TableRow key={index}>
                                            <TableCell colSpan={table.getFlatHeaders().length}>
                                                <Skeleton />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    Errored={(error) => (
                                        <TableRow>
                                            <TableCell colSpan={table.getFlatHeaders().length}>
                                                <Typography variant="body2" color="error">
                                                    {getApiErrorMessage(error)}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                    Empty={null}
                                >
                                    {() => {
                                        const rows = table.getRowModel().rows
                                        const hasRows = rows.length > 0

                                        if (!hasRows) {
                                            return (
                                                <TableRow>
                                                    <TableCell
                                                        colSpan={table.getFlatHeaders().length}
                                                        align="center"
                                                    >
                                                        {t(
                                                            'gift_with_purchase:dialog_search_products.no_products'
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            )
                                        }

                                        return (
                                            <>
                                                {rows.map((row) => (
                                                    <TableRow key={row.id}>
                                                        {row.getVisibleCells().map((cell) => (
                                                            <TableCell
                                                                key={cell.id}
                                                                padding={
                                                                    cell.column.columnDef.id ===
                                                                    'checkbox'
                                                                        ? 'checkbox'
                                                                        : undefined
                                                                }
                                                            >
                                                                {flexRender(
                                                                    cell.column.columnDef.cell,
                                                                    cell.getContext()
                                                                )}
                                                            </TableCell>
                                                        ))}
                                                    </TableRow>
                                                ))}
                                                {query.hasNextPage && !query.isFetchingNextPage && (
                                                    <TableRow
                                                        ref={lastRowRef}
                                                        key="intersection-observer-target"
                                                    >
                                                        <TableCell
                                                            colSpan={table.getFlatHeaders().length}
                                                        >
                                                            <Skeleton />
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </>
                                        )
                                    }}
                                </QueryMatcher>
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button color="inherit" onClick={onClose}>
                    {t('gift_with_purchase:dialog_search_products.cancel')}
                </Button>
                <Button
                    color="secondary"
                    variant="contained"
                    disabled={!hasSelectedProducts}
                    onClick={() => {
                        onAdd?.(data.filter((row) => selectedProductIds.has(row.id)))
                        onClose()
                    }}
                >
                    {t('gift_with_purchase:dialog_search_products.add')}
                </Button>
            </DialogActions>
        </Dialog>
    )
}
